import 'yup-phone-lite';

import { ChangeEvent, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Link from 'next/link';
import * as Yup from 'yup';

import Hidden from '~/assets/img/login/hidden.svg';
import Seen from '~/assets/img/login/shown.svg';
import { RegisterTrackingKey } from '~/components/views/Signup/keys';
import { AnalyticsEventNames } from '~/constants/AnalyticsEventMap';
import { SentryHelper } from '~/helpers/Sentry/SentryHelper';
import { LocalStorageHelper, SessionStorageHelper } from '~/helpers/StorageHelper';
import { VinovestNetworkingHelper } from '~/helpers/VinovestNetworkingHelper';
import { useTracking } from '~/hooks/tracking/useTracking';
import { baseRoutes } from '~/routes/applicationRoutes';
import { OktaAuthClient } from '~/utils/oktaAuthClient';
import { ROUTE_PATHS } from '~/utils/routePaths';
import { Button, Placeholder, Text } from '~/vinovest-design-system/components';
import { Input } from '~/vinovest-design-system/components/Form/components/Input';
import { dispatchError } from '~/containers/ErrorContainer/errorEventListener';
import { isValidPhoneNumber } from 'libphonenumber-js';

const validationSchemaNew = Yup.object().shape({
    firstName: Yup.string()
        .matches(/^[A-Za-z]+(?: [A-Za-z]+)+ ?$/, 'Please enter a first and last name')
        .required('Full name is required'),
    email: Yup.string().required('Email is required').email('Please enter a valid email'),
    password: Yup.string().required('Password is required').min(8, '')
});

interface PasswordValidation {
    hasCapitalLetter: boolean;
    hasLowercaseLetter: boolean;
    hasNumber: boolean;
    hasMinChar: boolean;
    hasSpecialChar: boolean;
}

interface FormObject {
    firstName: string;
    lastName: string;
    email: string;
    /** Phone number including country code */
    phone?: string;
    ['phone:countrycode']?: string;
    password: string;
    referralSource: string;
}

export const CreateAccountForm = () => {
    LocalStorageHelper.setItem('is_new_user_phone', true);

    const formMethods = useForm<FormObject>({
        mode: 'onChange',
        resolver: yupResolver(validationSchemaNew)
    });
    const { handleSubmit, formState } = formMethods;
    const [errorMessage, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [show, setShow] = useState(false);
    const [showPasswordReqs, setShowPasswordReqs] = useState(false);
    const [validPasswordReqs, setValidPasswordReqs] = useState(false);
    const [passwordAttributes, setPasswordAttributes] = useState<PasswordValidation>({
        hasCapitalLetter: false,
        hasNumber: false,
        hasLowercaseLetter: false,
        hasMinChar: false,
        hasSpecialChar: false
    });
    const { captureEvent } = useTracking();
    const [lastname, setLastname] = useState('');
    const [firstname, setFirstname] = useState('');

    const updateName = (event: ChangeEvent<HTMLInputElement>) => {
        // Update the lastname and firstname state with the new value
        const fullname = event.target.value.split(' ', 2);
        setFirstname(fullname[0]);
        setLastname(fullname[1] ? fullname[1] : '-');
    };

    const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        setPasswordAttributes({
            hasCapitalLetter: /[A-Z]/.test(value),
            hasLowercaseLetter: /[a-z]/.test(value),
            hasNumber: /\d/.test(value),
            hasMinChar: value.length >= 8,
            hasSpecialChar: /[^\dA-Za-z]+/.test(value)
        });
    };

    useEffect(() => {
        if (
            passwordAttributes.hasCapitalLetter === true &&
            passwordAttributes.hasLowercaseLetter === true &&
            passwordAttributes.hasNumber === true &&
            passwordAttributes.hasMinChar === true &&
            passwordAttributes.hasSpecialChar === true
        ) {
            setValidPasswordReqs(true);
            setShowPasswordReqs(false);
        }
    }, [
        passwordAttributes.hasCapitalLetter,
        passwordAttributes.hasLowercaseLetter,
        passwordAttributes.hasNumber,
        passwordAttributes.hasMinChar,
        passwordAttributes.hasSpecialChar
    ]);

    const onSubmit = async (values: FormObject) => {
        setShow(false);
        try {
            setLoading(true);
            const okta = await OktaAuthClient.getInstance();
            const { managed } = VinovestNetworkingHelper.getUnauthenticatedClient();
            const result = await managed.createNewUser({
                email: values.email,
                firstName: firstname,
                lastName: lastname,
                password: values.password,
                primaryPhone: '',
                login: values.email
            });
            if (result.ok) {
                LocalStorageHelper.setItem(RegisterTrackingKey, AnalyticsEventNames.EmailSignUpClick);
                LocalStorageHelper.setItem('is_new_user', true);
                const loginResult = await okta.signInWithCredentials({
                    username: values.email,
                    password: values.password
                });
                SessionStorageHelper.setItem('redirectUri', baseRoutes.createAccountPhone.href);
                if (loginResult.sessionToken) {
                    void okta.signInWithRedirect({ sessionToken: loginResult.sessionToken });
                }
            } else {
                const message = result.data?.message || '';
                if (message.includes('requirements were not met')) {
                    setError(
                        'Password must be at least 8 characters one number one uppercase letter and one special character'
                    );
                } else if (message.includes('already associated with an account')) {
                    setError('An account already exists for this email. Please try signing in.');
                    captureEvent(AnalyticsEventNames.AccountExistsError, { email: values.email });
                } else {
                    setError(message);
                }
            }
        } catch (error) {
            SentryHelper.captureException(error, { extra: { flow: 'signup' } });
            dispatchError('Error signing up, please try again.');
        } finally {
            setLoading(false);
        }
    };

    const phone = formMethods.getValues('phone');

    useEffect(() => {
        if (!formState.touchedFields.phone || formState.isValid) {
            formMethods.clearErrors('phone');
            return;
        }
        const validPhone = isValidPhoneNumber(phone ?? '');
        if (!validPhone) {
            // this isn't happening automatically.... this is my disappointed face
            formMethods.setError('phone', { type: 'phone', message: 'Please enter a valid phone number' });
        } else {
            formMethods.clearErrors('phone');
        }
    }, [formMethods, formState.isValid, formState.touchedFields.phone, phone]);

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)} className={`mx-auto overflow-hidden max-w-xl md:px-4 xs:px-0`}>
                <div className={'flex flex-row gap-4'}>
                    <div className={'flex-1'}>
                        <Input
                            inputProps={{ autoFocus: true }}
                            label={'Full Name'}
                            name={'firstName'}
                            placeholder={'Full Name'}
                            labelClasses={'xs:hidden md:flex'}
                            onInput={updateName}
                            inputClasses={
                                '!border rounded bg-neutrals-100 h-[50px] mt-1 align-center inline-flex  text-16 min-w-full xs:max-w-[125px]'
                            }
                        />
                    </div>
                    <div className={'flex-1 hidden'}>
                        <Input
                            inputProps={{ autoFocus: false }}
                            label={'Last Name'}
                            name={'lastName'}
                            placeholder={'Last Name'}
                            labelClasses={'xs:hidden md:flex'}
                            value={lastname}
                            inputClasses={`!border rounded bg-neutrals-100  h-[50px] mt-1 align-center inline-flex  text-16 min-w-full xs:max-w-[125px]`}
                        />
                    </div>
                </div>
                <div className={'flex flex-col sm:flex-row gap-4'}>
                    <div className={'flex-1'}>
                        <Input
                            inputProps={{ autoFocus: false }}
                            label={'Email'}
                            name={'email'}
                            placeholder={'Email'}
                            labelClasses={'xs:hidden md:flex'}
                            inputClasses={
                                '!border rounded bg-neutrals-100  h-[50px] mt-1 align-center inline-flex  text-16 min-w-full'
                            }
                        />
                    </div>
                </div>
                <div className={'flex flex-col sm:flex-row gap-4'}>
                    <div className={'flex-1 relative'}>
                        <Input
                            autoFocus
                            inputProps={{
                                autoFocus: false
                            }}
                            textClasses={'px-4'}
                            label={'Password'}
                            name={'password'}
                            type={`${!show ? 'password' : 'text'}`}
                            placeholder={'Password'}
                            labelClasses={'xs:hidden md:flex'}
                            inputClasses={
                                '!border bg-neutrals-100 rounded h-[50px] mt-1 align-center inline-flex  text-16 min-w-full ph-no-capture'
                            }
                            onInput={handlePasswordChange}
                            onFocus={() => {
                                setShowPasswordReqs(true);
                            }}
                        >
                            <button
                                type={'button'}
                                onClick={() => {
                                    setShow(!show);
                                }}
                                className={' absolute right-3 xs:top-[15px] md:top-[49px]'}
                            >
                                <img
                                    alt={'show or hide password icon button'}
                                    src={!show ? Seen.src : Hidden.src}
                                    className={' h-[25px] w-[25px]'}
                                />
                            </button>
                        </Input>
                        {showPasswordReqs && (
                            <div className={'px-4 py-6 bg-neutrals-100 -mt-6 text-gray-800 text-14'}>
                                <ul className={'mt-2'}>
                                    <li
                                        className={`flex items-center mb-2 ${
                                            passwordAttributes.hasSpecialChar
                                                ? 'text-green-600 animate-fade-hide'
                                                : 'text-gray-800'
                                        }`}
                                    >
                                        <i
                                            className={`pt-[2px] inline-flex items-center justify-center not-italic w-[15px] h-[15px] rounded-full mr-2 text-12 text-white ${
                                                passwordAttributes.hasSpecialChar
                                                    ? 'bg-green-600 rotate-[10deg]'
                                                    : 'bg-[red] pb-[3px]'
                                            }`}
                                        >
                                            {passwordAttributes.hasSpecialChar ? <>&#x2713;</> : 'x'}
                                        </i>
                                        Add a special character (!$#@)
                                    </li>
                                    <li
                                        className={`flex items-center mb-2 ${
                                            passwordAttributes.hasNumber
                                                ? 'text-green-600 animate-fade-hide'
                                                : 'text-gray-800'
                                        }`}
                                    >
                                        <i
                                            className={`pt-[2px] inline-flex items-center justify-center not-italic w-[15px] h-[15px] rounded-full mr-2 text-12 text-white ${
                                                passwordAttributes.hasNumber
                                                    ? 'bg-green-600 rotate-[10deg]'
                                                    : 'bg-[red] pb-[3px]'
                                            }`}
                                        >
                                            {passwordAttributes.hasNumber ? <>&#x2713;</> : 'x'}
                                        </i>
                                        Add a number
                                    </li>
                                    <li
                                        className={`flex items-center mb-2 ${
                                            passwordAttributes.hasCapitalLetter
                                                ? 'text-green-600 animate-fade-hide'
                                                : 'text-gray-800'
                                        }`}
                                    >
                                        <i
                                            className={`pt-[2px] inline-flex items-center justify-center not-italic w-[15px] h-[15px] rounded-full mr-2 text-12  text-white ${
                                                passwordAttributes.hasCapitalLetter
                                                    ? 'bg-green-600 rotate-[10deg]'
                                                    : 'bg-[red] pb-[3px]'
                                            }`}
                                        >
                                            {passwordAttributes.hasCapitalLetter ? <>&#x2713;</> : 'x'}
                                        </i>
                                        Add an uppercase letter
                                    </li>
                                    <li
                                        className={`flex items-center mb-2 ${
                                            passwordAttributes.hasLowercaseLetter
                                                ? 'text-green-600 animate-fade-hide'
                                                : 'text-gray-800'
                                        }`}
                                    >
                                        <i
                                            className={`pt-[2px] inline-flex items-center justify-center not-italic w-[15px] h-[15px] rounded-full mr-2 text-12  text-white ${
                                                passwordAttributes.hasLowercaseLetter
                                                    ? 'bg-green-600 rotate-[10deg]'
                                                    : 'bg-[red] pb-[3px]'
                                            }`}
                                        >
                                            {passwordAttributes.hasLowercaseLetter ? <>&#x2713;</> : 'x'}
                                        </i>
                                        Add a lowercase letter
                                    </li>
                                    <li
                                        className={`flex items-center mb-2 ${
                                            passwordAttributes.hasMinChar
                                                ? 'text-green-600 animate-fade-hide'
                                                : 'text-gray-800'
                                        }`}
                                    >
                                        <i
                                            className={`pt-[2px] inline-flex items-center justify-center not-italic w-[15px] h-[15px] rounded-full mr-2 text-12  text-white ${
                                                passwordAttributes.hasMinChar
                                                    ? 'bg-green-600 rotate-[10deg]'
                                                    : 'bg-[red] pb-[3px]'
                                            }`}
                                        >
                                            {passwordAttributes.hasMinChar ? <>&#x2713;</> : 'x'}
                                        </i>
                                        Must be at least 8 characters
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
                <Placeholder renderCondition={Boolean(errorMessage)} placeholder={null}>
                    <span className={'text-red-600'}>{errorMessage}</span>
                </Placeholder>
                <div className={'w-full flex justify-center mt-8'}>
                    <Button
                        type={'submit'}
                        variant={'green'}
                        classnames={'flex-1 cursor-pointer'}
                        disabled={!formState.isValid || loading || validPasswordReqs === false}
                        title={
                            (!validPasswordReqs && 'Password requirements not met') ||
                            (!formState.isValid && 'Please fill out all fields') ||
                            undefined
                        }
                    >
                        {loading ? 'Creating...' : 'Create Account'}
                    </Button>
                </div>

                <div className={'mt-2'}>
                    <Text
                        classnames={'mt-8 mb-4 text-11 max-w-[25rem] mx-auto text-center pl-2 pr-2'}
                        color={'text-[#A3A3A3]'}
                    >
                        Signing up for the Vinovest account means you agree to the{' '}
                        <Link href={ROUTE_PATHS.privacyPolicy} passHref legacyBehavior>
                            <span className={'text-platinum-800'}>Privacy Policy</span>
                        </Link>{' '}
                        and{' '}
                        <Link href={ROUTE_PATHS.terms}>
                            <span className={'text-platinum-800 font-normal'}>Terms of Service.</span>
                        </Link>
                    </Text>
                </div>
            </form>
        </FormProvider>
    );
};
